import { restApi } from '../../../api/restApiSlice';
import { appendPagesScopeParams, type DiscoverabilityPagesScope } from './discoverabilityPagesScope';

export type PagesToReviewTab = 'access_blocked' | 'access_limited' | 'all_pages';

export type PagesToReviewSortColumn = 'url' | 'ai_crawler_visits' | 'status';

export type PagesToReviewSortDirection = 'asc' | 'desc';

export const URL_SEARCH_CONDITIONS = [
  'contains',
  'not_contains',
  'equals',
  'not_equals',
  'starts_with',
  'ends_with',
] as const;

export type PagesToReviewUrlSearchCondition = typeof URL_SEARCH_CONDITIONS[number];

export type PagesToReviewQueryParams = DiscoverabilityPagesScope & {
  tab: PagesToReviewTab;
  page?: number;
  pageSize?: number;
  sortBy?: PagesToReviewSortColumn;
  sortDirection?: PagesToReviewSortDirection;
  urlSearch?: string;
  urlSearchCondition?: PagesToReviewUrlSearchCondition;
  statusEquals?: number;
  statusFrom?: number;
  statusTo?: number;
};

/**
 * The export takes the same scope and filters as the paged read, minus
 * pagination — that absence is the point: one request returns every matching
 * row. Unlike the paged read it travels as a JSON body, so a long domain list or
 * URL search can never hit a URL-length ceiling.
 */
export type PagesToReviewExportParams = Omit<
  PagesToReviewQueryParams,
  'page' | 'pageSize'
>;

export type PageToReview = {
  url: string;
  aiCrawlerVisits: number;
  lastStatusCode: number;
};

export type PagesToReviewResponse = {
  items: PageToReview[];
  totalCount: number;
};

export type PagesToReviewCounts = {
  accessBlocked: number;
  accessLimited: number;
};

export type PagesToReviewCountsQueryParams = DiscoverabilityPagesScope;

function buildQueryString(params: PagesToReviewQueryParams): string {
  const search = new URLSearchParams();
  appendPagesScopeParams(search, params);
  search.append('tab', params.tab);
  if (params.page !== undefined) search.append('page', String(params.page));
  if (params.pageSize !== undefined) {
    search.append('pageSize', String(params.pageSize));
  }
  if (params.sortBy) search.append('sortBy', params.sortBy);
  if (params.sortDirection) search.append('sortDirection', params.sortDirection);
  if (params.urlSearch !== undefined) {
    search.append('urlSearch', params.urlSearch);
    // The condition only qualifies an active search — never send it alone.
    if (params.urlSearchCondition) {
      search.append('urlSearchCondition', params.urlSearchCondition);
    }
  }
  if (params.statusEquals !== undefined) {
    search.append('statusEquals', String(params.statusEquals));
  }
  if (params.statusFrom !== undefined) {
    search.append('statusFrom', String(params.statusFrom));
  }
  if (params.statusTo !== undefined) {
    search.append('statusTo', String(params.statusTo));
  }
  return search.toString();
}

function buildCountsQueryString(params: PagesToReviewCountsQueryParams): string {
  const search = new URLSearchParams();
  appendPagesScopeParams(search, params);
  return search.toString();
}

const discoverabilityPagesApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getDiscoverabilityPages: builder.query<PagesToReviewResponse, PagesToReviewQueryParams>({
      query: (params) => ({
        url: `/v3/ai-insights/discoverability/pages?${buildQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: ['DiscoverabilityPages'],
    }),
    // Returns CSV text, not JSON — `responseHandler` keeps fetchBaseQuery from
    // trying to parse the body. `retry()` in the shared base query only retries
    // GETs, so a failed export never silently fires a second multi-megabyte
    // request.
    exportDiscoverabilityPages: builder.mutation<string, PagesToReviewExportParams>({
      query: (params) => ({
        url: '/v3/ai-insights/discoverability/pages/csv-export',
        method: 'POST',
        body: params,
        responseHandler: (response: Response) => response.text(),
      }),
    }),
    getDiscoverabilityPagesCounts: builder.query<
      PagesToReviewCounts,
      PagesToReviewCountsQueryParams
    >({
      query: (params) => ({
        url: `/v3/ai-insights/discoverability/pages/counts?${buildCountsQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: ['DiscoverabilityPages'],
    }),
  }),
});

export { discoverabilityPagesApi };
export const {
  useGetDiscoverabilityPagesQuery,
  useGetDiscoverabilityPagesCountsQuery,
  useExportDiscoverabilityPagesMutation,
} = discoverabilityPagesApi;
